import React, { useEffect, useState, useRef } from 'react';
import {useParams, Link, useLocation, useNavigate} from 'react-router-dom';
import { FaArrowLeft, FaPlay, FaSearch, FaTimes } from 'react-icons/fa';
import api from '../services/api';
import API_CONFIG from '../config/api';
import HeaderWrapper from '../components/common/HeaderWrapper';
import PostCard from '../components/PostCard';
import TrackList from '../components/TrackList';
import VideoList from '../components/VideoList';
import PaywallModal from '../components/paywall/PaywallModal';
import LoginModal from '../components/auth/LoginModal';
import RegisterModal from '../components/auth/RegisterModal';
import ModalPortal from '../components/common/ModalPortal';
import useContentAccess from '../hooks/useContentAccess';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import './PostDetailsPage.css';

const PostDetailsPage = () => {
    const { postId } = useParams();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageTitle, setPageTitle] = useState('Posts');
    const [isVideoOnlyPage, setIsVideoOnlyPage] = useState(false);
    const [heroVideo, setHeroVideo] = useState(null);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [showPaywallModal, setShowPaywallModal] = useState(false);
    const [selectedContent, setSelectedContent] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const videoRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { refreshBalance } = useCredits();

    // Access control hook
    const {checkAccess} = useContentAccess();

    // Handle back navigation
    const handleGoBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    const handleLoginRequired = () => {
        navigate('/login', { state: { from: window.location.pathname } });
    };

    // Paywall check callback passed to TrackList — called before streaming a paid track.
    // Returns true if playback should proceed, false if the modal was shown instead.
    const handleTrackPaywallCheck = async (track) => {
        const isCreator = user && track.creator && user.id === track.creator.id;
        const hasPurchaseAccess = await checkAccess(track.id, 'audio', isCreator);
        if (!hasPurchaseAccess) {
            setSelectedContent({
                id: track.id,
                type: 'audio',
                title: track.title,
                price: track.price
            });
            setShowPaywallModal(true);
            return false;
        }
        return true;
    };

    // Handle video playback with paywall check
    const handleVideoPlay = async (video, index = 0) => {
        if (!video) {
            setHeroVideo(null);
            setCurrentVideoIndex(0);
            return;
        }
        try {
            // Check if video is premium
            if (video.is_paid) {
                const isCreator = user && video.creator && user.id === video.creator.id;
                const hasPurchaseAccess = await checkAccess(video.id, 'video', isCreator);

                if (!hasPurchaseAccess) {
                    // Show paywall modal for video
                    setSelectedContent({
                        id: video.id,
                        type: 'video',
                        title: video.title,
                        price: video.price
                    });
                    setShowPaywallModal(true);
                    return;
                }
            }

            // Proceed with playback if free or purchased
            const videoPostId = video.id;

            const videoResponse = await api.get(`/videos/post/${videoPostId}`);
            const videoRecord = videoResponse.data;

            if (!videoRecord) {
                console.error('No video found for post ID:', videoPostId);
                return;
            }

            const videoId = videoRecord.id;
            console.log('Found video ID:', videoId);

            const response = await api.get(`/videos/${videoId}/stream`);
            console.log('Video stream API response:', response.data);

            const streamUrl = response.data.stream_url;

            if (!streamUrl) {
                console.error('No stream URL in response:', response.data);
                return;
            }

            setHeroVideo({
                ...video,
                streamUrl: streamUrl,
                videoData: videoRecord
            });
            setCurrentVideoIndex(index);

        } catch (error) {
            console.error('Error in handleVideoPlay:', error);
            let errorMessage = `Failed to play video: ${video.title || 'Unknown'}`;
            if (error.response) {
                errorMessage += `\nServer error: ${error.response.status}`;
                console.error('Error response:', error.response.data);
            } else if (error.request) {
                errorMessage += '\nNo response from server.';
            } else {
                errorMessage += `\n${error.message}`;
            }
            console.error('Error message:', errorMessage);
        }
    };

    const handlePurchaseSuccess = () => {
        // Refresh content access after successful purchase
        if (selectedContent) {
            checkAccess(selectedContent.id, selectedContent.type);
        }
        setShowPaywallModal(false);

        // Store the content type before clearing selectedContent
        const contentType = selectedContent?.type;
        const contentId = selectedContent?.id;

        setSelectedContent(null);

        // If it was a track, the TrackList will retry playback automatically
        // since onPaywallCheck will now return true on the next play click.

        // If it was a video, refresh the video player
        if (contentType === 'video') {
            const video = posts.find(p => p.id === contentId);
            if (video) {
                const index = posts.findIndex(p => p.id === contentId);
                handleVideoPlay(video, index);
            }
        }

        // If it was a post, we might want to refresh or show a success message
        if (contentType === 'post') {
            // Optionally refresh the page or show a success message
            console.log('Post purchased successfully');
            // You could trigger a re-render of the post content here
        }
    };

    useEffect(() => {
        const videoElement = videoRef.current;
        return () => {
            if (videoElement) {
                videoElement.pause();
            }
        };
    }, []);

    useEffect(() => {
        const getPageTitleFromUrl = () => {
            const pathParts = location.pathname.split('/').filter(part => part !== '');

            if (pathParts.length > 0) {
                const slug = pathParts[0];
                const titleCased = slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
                return `${titleCased} Posts`;
            }
            return 'Posts';
        };

        const fetchPostDetails = async (useCorsProxy = false) => {
            try {
                let response;
                if (Number.isInteger(Number(postId))) {
                    response = await api.get(`/posts/${postId}`);
                } else {
                    response = await api.get(`/posts/page/${postId}?skip=0&limit=100&published=true`);
                }

                let allPosts = [];

                if (Array.isArray(response.data)) {
                    allPosts = response.data;
                    console.log(`Found ${allPosts.length} posts in the API response array`);

                    if (allPosts.length > 0) {
                        const firstPost = allPosts[0];
                        console.log('First post:', firstPost);
                        const pageTitle = getPageTitleFromUrl();
                        setPageTitle(pageTitle);
                    }
                }
                else if (response.data && response.data.posts && Array.isArray(response.data.posts)) {
                    allPosts = response.data.posts;
                    console.log(`Found ${allPosts.length} posts in the response.data.posts array`);

                    if (response.data.title) {
                        setPageTitle(response.data.title);
                    } else if (response.data.name) {
                        setPageTitle(response.data.name);
                    }
                }
                else if (response.data && (response.data.id || response.data.page_id)) {
                    allPosts = [response.data];
                    console.log('Found a single post in the API response');

                    if (response.data.title) {
                        setPageTitle(response.data.title);
                    }
                }

                if (allPosts.length > 0) {
                    setPosts(allPosts);
                    const allVideos = allPosts.every(post => post.post_type === 'video');
                    setIsVideoOnlyPage(allVideos);
                } else {
                    // Redirect to privacy-policy page when no posts found
                    navigate('/under-construction', { replace: true });
                    return;
                }

                setLoading(false);
            } catch (err) {
                console.error('Error fetching post details:', err);

                if (!useCorsProxy && (err.message.includes('CORS') || err.message.includes('Network Error'))) {
                    console.log('Possible CORS issue detected, retrying with proxy...');
                    return fetchPostDetails(true);
                }

                let errorMessage = 'Failed to load post details';

                if (err.response) {
                    errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
                    console.error('Error response data:', err.response.data);
                } else if (err.request) {
                    errorMessage = 'No response received from server. Please check your connection.';
                } else {
                    errorMessage = `Error: ${err.message}`;
                }

                setError(errorMessage);
                setLoading(false);
            }
        };

        fetchPostDetails();
    }, [postId, location.pathname, navigate]);

    // Filter videos based on search query
    const filteredPosts = searchQuery
        ? posts.filter(post => post.title?.toLowerCase().includes(searchQuery.toLowerCase()))
        : posts;

    // Render a post with paywall check
    const renderPost = (post, index) => {
        if (post.post_type === 'audio' || post.post_type === 'music') {
            return (
                <div className="audio-post-container" key={post.id || `post-${index}`}>
                    {post.content && (
                        <div className="audio-post-description">
                            <p>{post.content}</p>
                        </div>
                    )}
                    <TrackList
                        tracks={[post]}
                        onPaywallCheck={handleTrackPaywallCheck}
                        loading={false}
                    />
                    {post.is_paid && (
                        <div className="premium-badge">Premium Track</div>
                    )}
                </div>
            );
        } else if (post.post_type === 'video') {
            return (
                <div className="video-post-container" key={post.id || `post-${index}`}>
                    {post.content && (
                        <div className="video-post-description">
                            <p>{post.content}</p>
                        </div>
                    )}
                    <VideoList
                        videos={[post]}
                        onVideoPlay={(video) => handleVideoPlay(video ? post : null, index)}
                        loading={false}
                    />
                    {post.is_paid && (
                        <div className="premium-badge">Premium Video</div>
                    )}
                </div>
            );
        } else {
            return (
                <div key={post.id || `post-${index}`} className="post-grid-item">
                    <PostCard post={post} />
                </div>
            );
        }
    };

    if (loading) return (
        <>
            <HeaderWrapper />
            <div className="post-details-container">
                <div className="post-details-content loading">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p className="loading-text">Loading category content...</p>
                    </div>
                </div>
            </div>
        </>
    );

    if (error) return (
        <>
            <HeaderWrapper />
            <div className="post-details-container">
                <div className="post-details-content error">
                    <h2>Error Loading Post</h2>
                    <p>{error}</p>
                    <div className="error-actions">
                        <button onClick={() => window.location.reload()} className="retry-button">
                            Retry
                        </button>
                        <button onClick={() => window.history.back()} className="back-button">
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <>
            {!isVideoOnlyPage && <HeaderWrapper />}
            <div className={`breadcrumb-bar ${isVideoOnlyPage ? 'video-page-minimal' : ''}`}>
                <button onClick={handleGoBack} className="breadcrumb-back-btn">
                    <FaArrowLeft /> Back
                </button>
                {isVideoOnlyPage && (
                    <div className="video-search-bar">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search videos..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        {searchQuery && (
                            <button
                                className="search-clear-btn"
                                onClick={() => setSearchQuery('')}
                                aria-label="Clear search"
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className={`post-details-container ${isVideoOnlyPage ? 'video-page-fullscreen' : ''}`}>
                {isVideoOnlyPage && heroVideo ? (
                    <>
                        <div className="hero-video-player">
                            <video
                                ref={videoRef}
                                src={heroVideo.streamUrl}
                                controls
                                autoPlay
                                className="hero-video"
                            />
                            <div className="hero-video-overlay">
                                <div className="hero-video-info">
                                    <h2 className="hero-video-title">{heroVideo.title}</h2>
                                    <p className="hero-video-category">{heroVideo.category_name || 'Video'}</p>
                                    {heroVideo.description && (
                                        <p className="hero-video-description">{heroVideo.description}</p>
                                    )}
                                </div>
                                <div className="hero-video-counter">
                                    {currentVideoIndex + 1} / {posts.length}
                                </div>
                            </div>
                        </div>

                        <div className="trending-playlist">
                            <h3 className="trending-title">Trending Now</h3>
                            <div className="trending-scroll-container">
                                {filteredPosts.map((post, index) => (
                                    <div
                                        key={post.id}
                                        className={`trending-card ${heroVideo.id === post.id ? 'active' : ''}`}
                                        onClick={() => handleVideoPlay(post, index)}
                                    >
                                        <div className="trending-card-thumbnail">
                                            {post.cover_image_url ? (
                                                <img
                                                    src={post.cover_image_url.startsWith('http') ? post.cover_image_url : `${API_CONFIG.BASE_URL}${post.cover_image_url}`}
                                                    alt={post.title}
                                                />
                                            ) : (
                                                <div className="trending-card-placeholder">
                                                    <FaPlay />
                                                </div>
                                            )}
                                            <div className="trending-card-play-icon">
                                                <FaPlay />
                                            </div>
                                        </div>
                                        <div className="trending-card-info">
                                            <h4 className="trending-card-title">{post.title}</h4>
                                            {post.duration && (
                                                <span className="trending-card-duration">
                                                    {Math.floor(post.duration / 60)}:{String(Math.floor(post.duration % 60)).padStart(2, '0')}
                                                </span>
                                            )}
                                            {post.is_paid && (
                                                <span className="premium-badge-small">Premium</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {filteredPosts.length === 0 && searchQuery && (
                                <div className="no-search-results">
                                    <p>No videos found</p>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                    <div className="posts-list-content">
                        <div className="posts-header">
                            {posts.some(post => post.post_type === 'audio' || post.post_type === 'music') && (
                                <div className="streaming-badge">● Now Streaming</div>
                            )}
                            <h1 className="page-title">{pageTitle}</h1>
                            <p className="posts-count"><span className="count-number">{posts.length}</span> posts found</p>
                        </div>

                        {posts.length > 0 ? (
                            <div className="posts-grid">
                                {posts.map((post, index) => renderPost(post, index))}
                            </div>
                        ) : (
                            <div className="no-posts-found">
                                <h2>No posts available</h2>
                                <p>There are no posts available for this page.</p>
                                <Link to="/" className="back-to-home">Back to Home</Link>
                            </div>
                        )}
                    </div>
                    {heroVideo && (
                        <div
                            className="video-modal-backdrop"
                            onClick={(e) => { if (e.target === e.currentTarget) handleVideoPlay(null); }}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 9999,
                                padding: '20px'
                            }}
                        >
                            <div
                                className="video-modal-content"
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    maxWidth: '900px',
                                    backgroundColor: '#000',
                                    borderRadius: '12px',
                                    overflow: 'hidden'
                                }}
                            >
                                <button
                                    onClick={() => handleVideoPlay(null)}
                                    aria-label="Close video"
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        cursor: 'pointer',
                                        color: '#fff',
                                        fontSize: '18px'
                                    }}
                                >
                                    ✕
                                </button>
                                {heroVideo.title && (
                                    <div
                                        className="video-modal-title"
                                        style={{
                                            color: '#fff',
                                            padding: '14px 60px 14px 16px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {heroVideo.title}
                                    </div>
                                )}
                                <video
                                    src={heroVideo.streamUrl}
                                    controls
                                    autoPlay
                                    style={{ width: '100%', display: 'block', maxHeight: '70vh' }}
                                />
                            </div>
                        </div>
                    )}
                    </>
                )}
            </div>

            {/* Reusable Paywall Modal - Supports all content types including posts */}
            {selectedContent && (
                <PaywallModal
                    isOpen={showPaywallModal}
                    onClose={() => {
                        setShowPaywallModal(false);
                        setSelectedContent(null);
                    }}
                    contentId={selectedContent.id}
                    contentType={selectedContent.type}
                    contentTitle={selectedContent.title}
                    price={selectedContent.price}
                    onPurchaseSuccess={handlePurchaseSuccess}
                    onLoginRequired={handleLoginRequired}
                />
            )}

            <ModalPortal isOpen={showLoginModal}>
                <LoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                    onLoginSuccess={async () => {
                        await refreshBalance();
                        setShowLoginModal(false);
                        // After successful login, check if we need to retry the purchase
                        if (selectedContent) {
                            // Retry the purchase or access check
                            setShowPaywallModal(true);
                        }
                    }}
                    onSwitchToRegister={() => {
                        setShowLoginModal(false);
                        setShowRegisterModal(true);
                    }}
                />
            </ModalPortal>
            <ModalPortal isOpen={showRegisterModal}>
                <RegisterModal
                    isOpen={showRegisterModal}
                    onClose={() => setShowRegisterModal(false)}
                    onBackToLogin={() => {
                        setShowRegisterModal(false);
                        setShowLoginModal(true);
                    }}
                />
            </ModalPortal>
        </>
    );
};

export default PostDetailsPage;